"use client";

import React, { useRef } from "react";
import Link from "next/link";
import Image from "next/image";

export interface TileItem {
  key: string;
  // Navigatie (standaard, bv. homepage) via href, óf een in-pagina filter
  // (bv. de shop-pagina) via onClick — precies één van de twee meegeven.
  href?: string;
  onClick?: () => void;
  active?: boolean; // alleen relevant bij onClick-tegels (filter aan/uit)
  label: string;
  image?: string; // ontbreekt bij categorieën zonder bannerImage in Firestore
  imageKey?: string; // optioneel data-i-key voor live-bewerking (alleen homepage geeft dit mee)
}

interface Props {
  tiles: TileItem[];
  title?: string; // optionele kop erboven, bv. "BEKIJK SUBCATEGORIEËN VAN DAMES"
  // Aantal tegels vooraan in de slider dat meteen en met hoge prioriteit laadt. Alleen
  // zetten waar de tegels het eerste zijn wat in beeld staat (de shop): daar is een tegel
  // het grootste element (LCP) en wachtte de foto op lazy loading. Het verborgen grid
  // (display:none) blijft lazy, anders haalt de browser elke foto dubbel op.
  eagerCount?: number;
}

// Herbruikbare categorie-tegels: op desktop/tablet een grid (zelfde visuele
// stijl als de bestaande "Subcategorieën Weergave" op de categoriepagina),
// op mobiel een horizontaal scrollende slider met pijltjes — zelfde
// scroll-aanpak als FeaturedProductsSlider (useRef + scrollBy smooth).
// Puur presentationeel: krijgt kant-en-klare tiles via props, doet zelf
// geen Firestore-calls. Grid- én slider-markup staan hier allebei altijd in
// de JSX (patroon uit ShopSidebar) — CSS @media bepaalt wat zichtbaar is,
// zodat er geen window.innerWidth-checks (en dus geen hydration-mismatch) nodig zijn.
export default function CategoryTileSlider({ tiles, title, eagerCount = 0 }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);

  if (!tiles || tiles.length === 0) return null;

  function scrollByPage(dir: 1 | -1) {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  return (
    <div className="category-tile-slider">
      {title && (
        <div className="category-tile-header">
          <h2 className="category-tile-title">{title}</h2>
          <div className="category-tile-arrows">
            <button type="button" className="category-tile-arrow" onClick={() => scrollByPage(-1)} aria-label="Vorige categorieën">‹</button>
            <button type="button" className="category-tile-arrow" onClick={() => scrollByPage(1)} aria-label="Volgende categorieën">›</button>
          </div>
        </div>
      )}

      {/* Zonder titel toch de pijltjes tonen (alleen zichtbaar op mobiel via CSS) */}
      {!title && (
        <div className="category-tile-arrows category-tile-arrows-standalone">
          <button type="button" className="category-tile-arrow" onClick={() => scrollByPage(-1)} aria-label="Vorige categorieën">‹</button>
          <button type="button" className="category-tile-arrow" onClick={() => scrollByPage(1)} aria-label="Volgende categorieën">›</button>
        </div>
      )}

      {/* ── DESKTOP/TABLET: grid ── */}
      <div className="category-tile-grid">
        {tiles.map((tile) => (
          <CategoryTile key={tile.key} tile={tile} />
        ))}
      </div>

      {/* ── MOBIEL: horizontaal scrollende slider ── */}
      <div className="category-tile-track" ref={trackRef}>
        {tiles.map((tile, index) => (
          <div className="category-tile-slide" key={tile.key}>
            <CategoryTile tile={tile} eager={index < eagerCount} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryTile({ tile, eager = false }: { tile: TileItem; eager?: boolean }) {
  const className = `category-tile-item${tile.active ? " category-tile-item-active" : ""}`;
  const content = (
    <>
      {tile.image && (
        <Image
          src={tile.image}
          data-i-key={tile.imageKey}
          alt={tile.label}
          fill
          sizes="(max-width: 768px) 45vw, (max-width: 1024px) 33vw, 25vw"
          style={{ objectFit: "cover" }}
          {...(eager ? { loading: "eager" as const, fetchPriority: "high" as const } : {})}
        />
      )}
      <div className="category-tile-overlay">
        <span className="category-tile-label">{tile.label}</span>
      </div>
    </>
  );

  // Filterknop (bv. shop-pagina): geen navigatie, alleen tileFilter aan/uit.
  if (tile.onClick) {
    return (
      <button type="button" onClick={tile.onClick} className={className} aria-pressed={!!tile.active}>
        {content}
      </button>
    );
  }

  if (!tile.image) {
    return (
      <Link href={tile.href || "#"} className="category-tile-item category-tile-placeholder">
        <span className="category-tile-placeholder-label">{tile.label}</span>
      </Link>
    );
  }

  return (
    <Link href={tile.href || "#"} className={className}>
      {content}
    </Link>
  );
}
